/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { getCustomChildItemTypes } from '../../registry/Manager';
import FormManager from '../Form/FormManager';
import { defaultValue } from './SearchForm';
import { helpRenders } from './SearchForm_Help';

const name = 'Search Form';

const SearchFormManager: ComponentManagerFn<'SearchForm'> = (
  platform: Platform,
): ComponentManager<'SearchForm'> => {
  const { isPropertyVisible, ...formMgr } = FormManager(
    platform,
    'SearchForm',
  ) as ComponentManager<'SearchForm'>;
  return {
    ...formMgr,
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('SearchForm'),
          'SearchCheckbox',
          'SearchDateField',
          'SearchLookupField',
          'SearchNumberField',
          'SearchSelectField',
          'SearchTextField',
        ] as ItemType[]
      ).includes(childType),
    name,
    isPropertyVisible(prop, itemData, ctx): boolean {
      switch (prop) {
        case 'plain':
          return false;
        default:
          return isPropertyVisible(prop, itemData, ctx);
      }
    },
    icon: { iconName: 'search' },
    category: 'Search',
    description: `**Overview**<br/>
The Search Form is a specialized container component designed to build query interfaces. Unlike a standard Form (which is used for data entry and editing), the Search Form is optimized for capturing criteria to filter datasets, typically displayed in a connected Data Grid, List, or Chart.
Use this component to create "Advanced Search" panels or filter bars that allow users to drill down into large datasets.

**1. Structure & Children**<br/>
The Search Form acts as a restrictive wrapper for specific search input fields. It ensures that only valid search components are used to maintain query integrity.
* **Default Child:** **FlexPanel** (used to organize search fields in a responsive row or grid layout).
* **Allowed Search Inputs:**
    * **Search Text Field:** For keyword or partial string matches.
    * **Search Number Field:** For exact amounts or numeric ranges.
    * **Search Date Field:** For specific days or timelines.
    * **Search Select Field:** For selecting from predefined categories or dropdowns.
    * **Search Checkbox:** For boolean flags (e.g., "Active Only").
    * **Search Lookup Field:** For searching and selecting related records from other data stores.

**2. Configuration**<br/>
* **Inheritance:** It inherits core functionality from the standard **Form Manager** but overrides specific data-handling behaviors to suit search workflows rather than data-submission workflows.
* **Hidden Properties:** It hides the "Plain" property, as search forms typically require a distinct visual container or standardized grouping to separate them from the result sets they control.

**3. Usage Strategy**<br/>
* **Placement:** Typically placed at the top of a page (as a horizontal bar) or inside a collapsible sidebar to maximize screen real estate.
* **Data Connection:** The Search Form inputs are bound to the same Data Store as the target component (e.g., a Data Grid). When a value is entered, the form prepares a filter object for that store.
* **Execution:** A "Search" or "Filter" button (often placed in a child **Toolbar**) triggers the query, applying all active criteria to the results display.

**4. Best Use Cases**<br/>
* **Order Management:** A panel allowing users to filter orders by "Customer Name" (Text), "Order Date" (Date), and "Total Amount" (Number).
* **Employee Directory:** A filter bar used to find staff by "Department" (Select) and "Work Location" (Lookup).
* **Report Generation:** A set of criteria fields that users must fill out to define the scope of a report before it is generated.
* **IoT Monitoring:** Filtering a list of sensors based on "Status" (Checkbox) and "Last Ping" (Date Field).`,
    helpRenders,
  };
};

export default SearchFormManager;
